// Service Worker Registration for Media Caching
export const registerServiceWorker = async (): Promise<void> => {
  if ('serviceWorker' in navigator) {
    try {
      const registration = await navigator.serviceWorker.register('/sw.js', {
        scope: '/'
      })
      console.log('Service Worker registered successfully:', registration.scope)

      // If there's a waiting worker (new version), tell it to activate immediately
      if (registration.waiting) {
        registration.waiting.postMessage({ type: 'SKIP_WAITING' })
      }

      // Listen for new service workers and auto-activate them
      registration.addEventListener('updatefound', () => {
        const newWorker = registration.installing
        if (newWorker) {
          newWorker.addEventListener('statechange', () => {
            if (newWorker.state === 'activated') {
              console.log('New Service Worker activated, media precached')
            }
          })
        }
      })
    } catch (error) {
      console.error('Service Worker registration failed:', error)
    }
  }
}

// Precache media files
export const precacheMedia = (urls: string[]): void => {
  if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
    navigator.serviceWorker.controller.postMessage({
      type: 'PRECACHE_MEDIA',
      urls
    })
  }
}

// Clear media cache
export const clearMediaCache = (): void => {
  if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
    navigator.serviceWorker.controller.postMessage({
      type: 'CLEAR_CACHE'
    })
  }
}

// Cache utility for optimizing API calls and photo loading
interface CacheItem<T> {
  data: T
  timestamp: number
  expiresIn: number
}

class CacheManager {
  private cache = new Map<string, CacheItem<any>>()
  private readonly DEFAULT_EXPIRE_TIME = 5 * 60 * 1000 // 5 minutes

  set<T>(key: string, data: T, expiresIn: number = this.DEFAULT_EXPIRE_TIME): void {
    this.cache.set(key, {
      data,
      timestamp: Date.now(),
      expiresIn
    })
  }

  get<T>(key: string): T | null {
    const item = this.cache.get(key)
    
    if (!item) {
      return null
    }

    const now = Date.now()
    if (now - item.timestamp > item.expiresIn) {
      this.cache.delete(key)
      return null
    }

    return item.data as T
  }

  has(key: string): boolean {
    const item = this.cache.get(key)
    if (!item) return false

    const now = Date.now()
    if (now - item.timestamp > item.expiresIn) {
      this.cache.delete(key)
      return false
    }

    return true
  }

  delete(key: string): void {
    this.cache.delete(key)
  }

  clear(): void {
    this.cache.clear()
  }

  // Clear specific patterns (useful for clearing empty photo caches)
  clearByPattern(pattern: string): void {
    const keysToDelete: string[] = []
    this.cache.forEach((_, key) => {
      if (key.includes(pattern)) {
        keysToDelete.push(key)
      }
    })
    keysToDelete.forEach(key => this.cache.delete(key))
  }

  // Generate cache keys
  generatePhotoKey(turbineId: number, roomIndex?: number): string {
    return `photos_${turbineId}_${roomIndex || 'all'}`
  }

  generateTurbineKey(turbineId: number): string {
    return `turbine_${turbineId}`
  }

  generateCompaniesKey(): string {
    return 'companies_list'
  }

  generateUsersKey(): string {
    return 'users_list'
  }
}

// Singleton instance
export const cacheManager = new CacheManager()

// Photo cache specifically for images
class PhotoImageCache {
  private imageCache = new Map<string, HTMLImageElement>()
  private loadingPromises = new Map<string, Promise<HTMLImageElement>>()

  async getImage(url: string): Promise<HTMLImageElement> {
    // Return cached image if available
    if (this.imageCache.has(url)) {
      return this.imageCache.get(url)!
    }

    // Return existing loading promise if already loading
    if (this.loadingPromises.has(url)) {
      return this.loadingPromises.get(url)!
    }

    // Create new loading promise
    const loadingPromise = new Promise<HTMLImageElement>((resolve, reject) => {
      const img = new Image()
      img.crossOrigin = 'anonymous'
      
      img.onload = () => {
        this.imageCache.set(url, img)
        this.loadingPromises.delete(url)
        resolve(img)
      }
      
      img.onerror = () => {
        this.loadingPromises.delete(url)
        reject(new Error(`Failed to load image: ${url}`))
      }
      
      img.src = url
    })

    this.loadingPromises.set(url, loadingPromise)
    return loadingPromise
  }

  preloadImages(urls: string[]): void {
    urls.forEach(url => {
      if (!this.imageCache.has(url) && !this.loadingPromises.has(url)) {
        this.getImage(url).catch(() => {
          // Silently fail for preloading
        })
      }
    })
  }

  delete(url: string): void {
    this.imageCache.delete(url)
    this.loadingPromises.delete(url)
  }

  clearCache(): void {
    this.imageCache.clear()
    this.loadingPromises.clear()
  }

  getCacheSize(): number {
    return this.imageCache.size
  }
}

export const photoImageCache = new PhotoImageCache() 